import dayjs from 'dayjs';
import {useMemo} from 'react';
import {useReservation} from 'context/reservation';
import {useSchema} from 'context/guest';
import {IV_PROCESS_TYPE} from '@chekin/core';

type UseIVOnPropertyReturn = {
  isIVOnProperty: boolean;
  isIVOnPropertyEnabled: boolean;
  isIVOnPropertyAvailable: boolean;
  isIVOnPropertyUnavailable: boolean;
};

/**
 * Hook to determine identity verification (IV) on-property process availability
 *
 * Evaluates the on-property IV process type and availability based on:
 * - Whether the property has on-property IV enabled
 * - Whether IV feature is globally enabled
 * - Whether the current date falls within the reservation check-in period
 *
 * @returns {UseIVOnPropertyReturn}
 *   - isIVOnProperty: Whether the property uses on-property verification process type
 *   - isIVOnPropertyEnabled: Whether on-property IV is fully enabled (feature enabled + process type is on-property)
 *   - isIVOnPropertyAvailable: Whether guest can proceed with on-property IV (enabled + within check-in period)
 *   - isIVOnPropertyUnavailable: Whether on-property IV is disabled due to being outside check-in period
 */
function useIVOnProperty(): UseIVOnPropertyReturn {
  const {data: reservation, housing} = useReservation();
  const {
    identityVerificationDetails: {enabled: ivEnabled},
  } = useSchema();

  const isIVOnProperty =
    housing?.identity_verification_process_type === IV_PROCESS_TYPE.onProperty;
  const isIVOnPropertyEnabled = ivEnabled && isIVOnProperty;

  const isChekInPeriod = useMemo(() => {
    if (!reservation) return false;
    const today = dayjs().utc().startOf('day');
    const checkin = dayjs(reservation.check_in_date).utc().startOf('day');
    const checkout = dayjs(reservation.check_out_date).utc().startOf('day');

    return today.isBetween(checkin, checkout, 'day', '[]');
  }, [reservation]);

  return useMemo(
    () => ({
      isIVOnProperty,
      isIVOnPropertyEnabled,
      isIVOnPropertyAvailable: isIVOnPropertyEnabled && isChekInPeriod,
      isIVOnPropertyUnavailable: isIVOnPropertyEnabled && !isChekInPeriod,
    }),
    [isIVOnProperty, isIVOnPropertyEnabled, isChekInPeriod],
  );
}

export {useIVOnProperty};
